"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  waitForDataChannelOpen,
  waitForIceGatheringComplete,
} from "@/lib/services/realtimeWebRtc";

const MICROPHONE_SETUP_TIMEOUT_MS = 10000;
const REALTIME_SESSION_TIMEOUT_MS = 12000;
const TRANSCRIPTION_TIMEOUT_MS = 10000;

function getUserMediaWithTimeout(constraints, signal) {
  let timedOut = false;
  let timeoutId;
  let handleAbort;
  const mediaRequest = navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      if (timedOut || signal?.aborted) {
        stream.getTracks().forEach((track) => track.stop());
        signal?.throwIfAborted();
        throw new Error("Microphone permission timed out");
      }
      return stream;
    });

  return Promise.race([
    mediaRequest,
    new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        timedOut = true;
        reject(new Error("Microphone permission timed out"));
      }, MICROPHONE_SETUP_TIMEOUT_MS);
    }),
    new Promise((_, reject) => {
      handleAbort = () => reject(signal.reason);
      if (signal.aborted) handleAbort();
      else signal.addEventListener("abort", handleAbort, { once: true });
    }),
  ]).finally(() => {
    clearTimeout(timeoutId);
    signal.removeEventListener("abort", handleAbort);
  });
}

export const useVoiceSearch = () => {
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [listeningText, setListeningText] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const [isVoiceQuery, setIsVoiceQuery] = useState(false);
  const [autoPlayResponse, setAutoPlayResponse] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const isCaptureActiveRef = useRef(false);
  const captureRequestRef = useRef(0);
  const speechAudioRef = useRef(null);
  const speechAudioUrlRef = useRef(null);
  const speechRequestRef = useRef(0);
  const audioContextRef = useRef(null);
  const audioPrimedRef = useRef(false);
  const connectPromiseRef = useRef(null);
  const connectionAbortRef = useRef(null);
  const onVoiceStartRef = useRef(null);
  const onResultRef = useRef(null);
  const onPartialResultRef = useRef(null);
  const latestTranscriptRef = useRef("");
  const transcriptDeltaBufferRef = useRef("");
  const emittedTranscriptRef = useRef(false);
  const endEmitOnlyRef = useRef(false);
  const awaitingTranscriptRef = useRef(false);
  const emitCommittedTranscriptRef = useRef(false);
  const listenWindowTimerRef = useRef(null);
  const transcriptionTimerRef = useRef(null);
  const speechPauseTimerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasWebRtc = typeof window.RTCPeerConnection !== "undefined";
    const hasMediaDevices = Boolean(navigator?.mediaDevices?.getUserMedia);
    setSpeechSupported(hasWebRtc && hasMediaDevices);

    return () => {
      captureRequestRef.current += 1;
      connectionAbortRef.current?.abort();
      isCaptureActiveRef.current = false;
      speechRequestRef.current += 1;
      clearTimeout(speechPauseTimerRef.current);
      if (listenWindowTimerRef.current) {
        clearTimeout(listenWindowTimerRef.current);
        listenWindowTimerRef.current = null;
      }
      if (transcriptionTimerRef.current) {
        clearTimeout(transcriptionTimerRef.current);
        transcriptionTimerRef.current = null;
      }

      const dc = dataChannelRef.current;
      if (dc) {
        try {
          dc.close();
        } catch {}
      }
      dataChannelRef.current = null;

      const pc = peerConnectionRef.current;
      if (pc) {
        try {
          pc.getSenders().forEach((sender) => sender.track?.stop());
        } catch {}
        try {
          pc.close();
        } catch {}
      }
      peerConnectionRef.current = null;

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      localStreamRef.current = null;
      localAudioTrackRef.current = null;

      const speechAudio = speechAudioRef.current;
      if (speechAudio) {
        try {
          speechAudio.pause();
          speechAudio.src = "";
        } catch {}
      }
      speechAudioRef.current = null;
      if (speechAudioUrlRef.current) {
        URL.revokeObjectURL(speechAudioUrlRef.current);
        speechAudioUrlRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
      }
      audioContextRef.current = null;
      audioPrimedRef.current = false;
      setIsAssistantSpeaking(false);
    };
  }, []);

  const setupVoiceRecognition = useCallback((onResult, onPartialResult) => {
    onResultRef.current = onResult;
    onPartialResultRef.current = onPartialResult;
  }, []);

  const primeAudioPlayback = useCallback(async () => {
    if (typeof window === "undefined") return false;
    if (audioPrimedRef.current) return true;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioCtx();
        }
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }
      }

      const unlockAudio = document.createElement("audio");
      unlockAudio.playsInline = true;
      unlockAudio.muted = true;
      unlockAudio.src =
        "data:audio/mp3;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAABAAADpAAACwAATGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//uQxAADBQABpAAAAnEAAANIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";
      document.body.appendChild(unlockAudio);
      try {
        await unlockAudio.play();
      } catch {}
      try {
        unlockAudio.pause();
        unlockAudio.currentTime = 0;
      } catch {}
      unlockAudio.remove();

      audioPrimedRef.current = true;
      return true;
    } catch {
      return false;
    }
  }, []);

  const emitTranscriptIfNeeded = useCallback(
    (transcript) => {
      const cleanTranscript = String(transcript || "").trim();
      if (!cleanTranscript) return;
      if (emittedTranscriptRef.current) return;
      if (typeof onResultRef.current !== "function") return;

      emittedTranscriptRef.current = true;
      setIsVoiceQuery(true);
      setAutoPlayResponse(true);
      onResultRef.current(cleanTranscript);
    },
    [setAutoPlayResponse, setIsVoiceQuery],
  );

  const stopVoiceCapture = useCallback(
    (options = {}) => {
      const { suppressPendingResult = true } = options;
      const wasCapturing = isCaptureActiveRef.current;
      if (!wasCapturing && !suppressPendingResult) return;
      captureRequestRef.current += 1;
      clearTimeout(speechPauseTimerRef.current);
      isCaptureActiveRef.current = false;
      onVoiceStartRef.current = null;

      if (listenWindowTimerRef.current) {
        clearTimeout(listenWindowTimerRef.current);
        listenWindowTimerRef.current = null;
      }
      if (transcriptionTimerRef.current) {
        clearTimeout(transcriptionTimerRef.current);
        transcriptionTimerRef.current = null;
      }

      const localTrack = localAudioTrackRef.current;
      if (localTrack) {
        localTrack.enabled = false;
      }

      const dc = dataChannelRef.current;
      awaitingTranscriptRef.current =
        !suppressPendingResult && dc?.readyState === "open";
      emitCommittedTranscriptRef.current = !suppressPendingResult;
      let commitFailed = false;
      if (wasCapturing && dc && dc.readyState === "open") {
        try {
          dc.send(
            JSON.stringify({
              type: suppressPendingResult
                ? "input_audio_buffer.clear"
                : "input_audio_buffer.commit",
            }),
          );
          if (!suppressPendingResult) {
            if (transcriptionTimerRef.current) {
              clearTimeout(transcriptionTimerRef.current);
            }
            transcriptionTimerRef.current = setTimeout(() => {
              if (!awaitingTranscriptRef.current) return;
              awaitingTranscriptRef.current = false;
              setIsTranscribing(false);
              emitCommittedTranscriptRef.current = false;
              setListeningText("");
              setVoiceError(
                "No transcript came back. Check your microphone and try again.",
              );
            }, TRANSCRIPTION_TIMEOUT_MS);
          }
        } catch (error) {
          console.error("Failed to commit voice transcription:", error);
          awaitingTranscriptRef.current = false;
          emitCommittedTranscriptRef.current = false;
          commitFailed = true;
          setVoiceError("Could not submit your voice. Please try again.");
        }
      }

      if (!suppressPendingResult && !awaitingTranscriptRef.current) {
        if (latestTranscriptRef.current) {
          emitTranscriptIfNeeded(latestTranscriptRef.current);
        } else if (!commitFailed) {
          setVoiceError(
            "Voice connection closed before transcription finished. Please try again.",
          );
        }
      }

      if (!awaitingTranscriptRef.current) {
        transcriptDeltaBufferRef.current = "";
        emitCommittedTranscriptRef.current = false;
        endEmitOnlyRef.current = false;
      }
      setIsListening(false);
      setIsTranscribing(Boolean(awaitingTranscriptRef.current));
      if (awaitingTranscriptRef.current) {
        setListeningText((current) => current || "Finishing transcription...");
      } else {
        setListeningText("");
      }
    },
    [emitTranscriptIfNeeded],
  );

  const extractTranscriptFromEvent = useCallback((event) => {
    if (!event || typeof event !== "object") return "";

    if (typeof event.transcript === "string") {
      return event.transcript;
    }

    if (typeof event.delta === "string") {
      return event.delta;
    }

    if (typeof event.text === "string") {
      return event.text;
    }

    const item = event.item;
    if (item && Array.isArray(item.content)) {
      for (const part of item.content) {
        if (typeof part?.transcript === "string") return part.transcript;
        if (typeof part?.text === "string") return part.text;
      }
    }

    return "";
  }, []);

  const handleRealtimeEvent = useCallback(
    (event) => {
      const eventType = String(event?.type || "");

      const isUserTranscriptEvent = eventType.includes(
        "input_audio_transcription",
      );
      if (
        !isUserTranscriptEvent ||
        (!isCaptureActiveRef.current && !awaitingTranscriptRef.current)
      ) {
        return;
      }

      if (eventType.endsWith(".failed")) {
        console.error(
          "Realtime transcription failed:",
          event.error?.message || eventType,
        );
        if (transcriptionTimerRef.current) {
          clearTimeout(transcriptionTimerRef.current);
          transcriptionTimerRef.current = null;
        }
        isCaptureActiveRef.current = false;
        awaitingTranscriptRef.current = false;
        setIsTranscribing(false);
        emitCommittedTranscriptRef.current = false;
        setIsListening(false);
        setListeningText("");
        if (localAudioTrackRef.current) {
          localAudioTrackRef.current.enabled = false;
        }
        setVoiceError(
          "Voice transcription failed. Please try again or type your search.",
        );
        return;
      }

      // Realtime often streams transcript as tiny deltas; buffer them and emit only
      // when a completed/full transcript event is received.
      if (eventType.endsWith(".delta")) {
        const delta = String(event?.delta || "");
        if (!delta) return;
        transcriptDeltaBufferRef.current += delta;
        latestTranscriptRef.current = transcriptDeltaBufferRef.current.trim();
        setListeningText(latestTranscriptRef.current);
        if (typeof onPartialResultRef.current === "function") {
          onPartialResultRef.current(latestTranscriptRef.current);
        }
        if (isCaptureActiveRef.current && !endEmitOnlyRef.current) {
          clearTimeout(speechPauseTimerRef.current);
          speechPauseTimerRef.current = setTimeout(() => {
            stopVoiceCapture({ suppressPendingResult: false });
          }, 1800);
        }
        return;
      }

      const completedTranscript = String(
        event?.transcript ||
          extractTranscriptFromEvent(event) ||
          transcriptDeltaBufferRef.current ||
          "",
      ).trim();

      if (!completedTranscript) return;

      if (transcriptionTimerRef.current) {
        clearTimeout(transcriptionTimerRef.current);
        transcriptionTimerRef.current = null;
      }
      latestTranscriptRef.current = completedTranscript;
      transcriptDeltaBufferRef.current = "";

      if (awaitingTranscriptRef.current) {
        awaitingTranscriptRef.current = false;
        setIsTranscribing(false);
        setListeningText("");
        if (emitCommittedTranscriptRef.current) {
          emitTranscriptIfNeeded(completedTranscript);
        }
        emitCommittedTranscriptRef.current = false;
        endEmitOnlyRef.current = false;
        return;
      }

      if (!endEmitOnlyRef.current) {
        stopVoiceCapture({ suppressPendingResult: true });
        emitTranscriptIfNeeded(completedTranscript);
        setIsListening(false);
        setListeningText("");
      }
    },
    [emitTranscriptIfNeeded, extractTranscriptFromEvent, stopVoiceCapture],
  );

  const connectRealtimeSession = useCallback(async () => {
    if (connectPromiseRef.current) {
      const previousController = connectionAbortRef.current;
      try {
        await connectPromiseRef.current;
      } catch (error) {
        if (!previousController?.signal.aborted) throw error;
      }
      if (!previousController?.signal.aborted) return;
    }
    const existingPc = peerConnectionRef.current;
    const existingDc = dataChannelRef.current;

    if (
      existingPc &&
      existingDc &&
      existingDc.readyState === "open" &&
      localAudioTrackRef.current?.readyState === "live" &&
      existingPc.connectionState !== "closed" &&
      existingPc.connectionState !== "failed"
    ) {
      return;
    }

    if (existingPc) {
      try {
        existingPc.getSenders().forEach((sender) => sender.track?.stop());
      } catch {}
      try {
        existingPc.close();
      } catch {}
      peerConnectionRef.current = null;
      dataChannelRef.current = null;

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      localStreamRef.current = null;
      localAudioTrackRef.current = null;
    }

    const controller = new AbortController();
    connectionAbortRef.current = controller;
    let pendingPeerConnection = null;
    let pendingDataChannel = null;
    let pendingStream = null;
    const closePendingConnection = () => {
      pendingDataChannel?.close();
      pendingPeerConnection?.close();
      pendingStream?.getTracks().forEach((track) => track.stop());
    };
    controller.signal.addEventListener("abort", closePendingConnection, {
      once: true,
    });
    connectPromiseRef.current = (async () => {
      const pc = new RTCPeerConnection();
      pendingPeerConnection = pc;

      const dc = pc.createDataChannel("oai-events");
      pendingDataChannel = dc;
      dc.addEventListener("message", (e) => {
        if (controller.signal.aborted) return;
        try {
          const parsed = JSON.parse(e.data);
          if (parsed?.type === "error") {
            const providerMessage = String(
              parsed.error?.message || "Realtime transcription failed",
            );
            console.error("Realtime transcription error:", providerMessage);
            setVoiceError(
              "Voice transcription failed. Please try again or type your search.",
            );
            isCaptureActiveRef.current = false;
            awaitingTranscriptRef.current = false;
            setIsTranscribing(false);
            emitCommittedTranscriptRef.current = false;
            setIsListening(false);
            setListeningText("");
            if (localAudioTrackRef.current) {
              localAudioTrackRef.current.enabled = false;
            }
            if (transcriptionTimerRef.current) {
              clearTimeout(transcriptionTimerRef.current);
              transcriptionTimerRef.current = null;
            }
            return;
          }
          handleRealtimeEvent(parsed);
        } catch (error) {
          console.error("Invalid realtime transcription event:", error);
        }
      });

      const stream = await getUserMediaWithTimeout(
        {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        },
        controller.signal,
      );
      pendingStream = stream;
      controller.signal.throwIfAborted();

      const [audioTrack] = stream.getAudioTracks();
      if (!audioTrack) {
        throw new Error("No audio track available");
      }

      audioTrack.enabled = false;
      pc.addTrack(audioTrack, stream);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGatheringComplete(pc, 10_000, controller.signal);
      controller.signal.throwIfAborted();
      const localSdp = pc.localDescription?.sdp;
      if (!localSdp) {
        throw new Error("WebRTC offer is missing its local SDP");
      }

      const response = await fetch("/api/search/realtime/session", {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: localSdp,
        signal: AbortSignal.any([
          controller.signal,
          AbortSignal.timeout(REALTIME_SESSION_TIMEOUT_MS),
        ]),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Realtime session failed: ${response.status} ${errorText}`,
        );
      }

      const answerSdp = await response.text();
      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });
      await waitForDataChannelOpen(dc, pc, 10_000, controller.signal);
      controller.signal.throwIfAborted();

      peerConnectionRef.current = pc;
      dataChannelRef.current = dc;
      localStreamRef.current = stream;
      localAudioTrackRef.current = audioTrack;
    })();

    try {
      await connectPromiseRef.current;
    } catch (error) {
      try {
        pendingDataChannel?.close();
      } catch {}
      try {
        pendingPeerConnection?.close();
      } catch {}
      pendingStream?.getTracks().forEach((track) => track.stop());
      peerConnectionRef.current = null;
      dataChannelRef.current = null;
      localStreamRef.current = null;
      localAudioTrackRef.current = null;
      throw error;
    } finally {
      controller.signal.removeEventListener("abort", closePendingConnection);
      connectPromiseRef.current = null;
    }
  }, [handleRealtimeEvent]);

  const cancelAssistantSpeech = useCallback(() => {
    speechRequestRef.current += 1;
    const audio = speechAudioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.src = "";
      } catch {}
    }
    speechAudioRef.current = null;
    if (speechAudioUrlRef.current) {
      URL.revokeObjectURL(speechAudioUrlRef.current);
      speechAudioUrlRef.current = null;
    }
    setIsAssistantSpeaking(false);
  }, []);

  const cancelVoiceSession = useCallback(() => {
    stopVoiceCapture({ suppressPendingResult: true });
    cancelAssistantSpeech();
    connectionAbortRef.current?.abort();
    dataChannelRef.current?.close();
    peerConnectionRef.current?.close();
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    dataChannelRef.current = null;
    peerConnectionRef.current = null;
    localStreamRef.current = null;
    localAudioTrackRef.current = null;
    setVoiceError("");
  }, [cancelAssistantSpeech, stopVoiceCapture]);

  const speakRealtimeText = useCallback(
    async (text) => {
      const cleanText = String(text || "").trim();
      if (!cleanText) return false;

      cancelAssistantSpeech();
      const requestId = speechRequestRef.current;
      try {
        const response = await fetch("/api/support/text-to-speech", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: cleanText,
          }),
        });
        if (!response.ok) {
          throw new Error(`Speech generation failed (${response.status})`);
        }

        const audioUrl = URL.createObjectURL(await response.blob());
        if (requestId !== speechRequestRef.current) {
          URL.revokeObjectURL(audioUrl);
          return false;
        }

        const audio = new Audio(audioUrl);
        speechAudioRef.current = audio;
        speechAudioUrlRef.current = audioUrl;
        const clearAudio = () => {
          if (speechAudioRef.current === audio) {
            speechAudioRef.current = null;
            speechAudioUrlRef.current = null;
            setIsAssistantSpeaking(false);
          }
          URL.revokeObjectURL(audioUrl);
        };
        audio.onplay = () => setIsAssistantSpeaking(true);
        audio.onended = clearAudio;
        audio.onerror = clearAudio;
        await audio.play();
        return true;
      } catch (error) {
        console.error("Speech generation error:", error);
        if (requestId === speechRequestRef.current) {
          setIsAssistantSpeaking(false);
        }
        return false;
      }
    },
    [cancelAssistantSpeech],
  );

  const startVoiceCapture = useCallback(
    async (onVoiceStart, options = {}) => {
      if (!speechSupported || typeof window === "undefined") {
        alert("Voice search is not supported in this browser");
        return false;
      }

      const maxListenMs = Number(options?.maxListenMs) || 0;
      const emitOnEndOnly = Boolean(options?.emitOnEndOnly);
      const requestId = ++captureRequestRef.current;

      setVoiceError("");
      setIsTranscribing(false);
      clearTimeout(speechPauseTimerRef.current);
      isCaptureActiveRef.current = false;
      onVoiceStartRef.current = onVoiceStart;
      endEmitOnlyRef.current = emitOnEndOnly;
      awaitingTranscriptRef.current = false;
      emitCommittedTranscriptRef.current = false;
      latestTranscriptRef.current = "";
      transcriptDeltaBufferRef.current = "";
      emittedTranscriptRef.current = false;

      try {
        await connectRealtimeSession();
        if (requestId !== captureRequestRef.current) return false;

        const localTrack = localAudioTrackRef.current;
        if (!localTrack) {
          throw new Error("Realtime audio track not available");
        }

        localTrack.enabled = true;
        isCaptureActiveRef.current = true;

        setIsListening(true);
        setListeningText("");

        if (listenWindowTimerRef.current) {
          clearTimeout(listenWindowTimerRef.current);
          listenWindowTimerRef.current = null;
        }

        if (maxListenMs > 0) {
          listenWindowTimerRef.current = setTimeout(() => {
            stopVoiceCapture({ suppressPendingResult: false });
          }, maxListenMs);
        }

        if (typeof onVoiceStartRef.current === "function") {
          onVoiceStartRef.current();
          onVoiceStartRef.current = null;
        }
        return true;
      } catch (error) {
        if (requestId !== captureRequestRef.current) return false;
        console.error("Realtime voice start failed:", error);
        setVoiceError(
          "Could not start voice search. Check microphone access and try again.",
        );
        isCaptureActiveRef.current = false;
        setIsListening(false);
        setListeningText("");
        setIsVoiceQuery(false);
        onVoiceStartRef.current = null;
        const localTrack = localAudioTrackRef.current;
        if (localTrack) {
          localTrack.enabled = false;
        }
        awaitingTranscriptRef.current = false;
        emitCommittedTranscriptRef.current = false;
        endEmitOnlyRef.current = false;

        if (listenWindowTimerRef.current) {
          clearTimeout(listenWindowTimerRef.current);
          listenWindowTimerRef.current = null;
        }
        return false;
      }
    },
    [connectRealtimeSession, speechSupported, stopVoiceCapture],
  );

  const prepareVoiceSession = useCallback(async () => {
    if (!speechSupported || typeof window === "undefined") {
      return false;
    }

    try {
      await connectRealtimeSession();
      return true;
    } catch (error) {
      console.warn("Realtime prewarm failed:", error);
      return false;
    }
  }, [connectRealtimeSession, speechSupported]);

  const handleVoiceSearch = useCallback(
    (onVoiceStart, options = {}) => {
      if (isListening) {
        stopVoiceCapture({ suppressPendingResult: false });
      } else {
        startVoiceCapture(onVoiceStart, options);
      }
    },
    [isListening, startVoiceCapture, stopVoiceCapture],
  );

  const resetVoiceFlags = useCallback(() => {
    setIsVoiceQuery(false);
    setAutoPlayResponse(false);
  }, []);

  return {
    isListening,
    isTranscribing,
    speechSupported,
    isAssistantSpeaking,
    listeningText,
    voiceError,
    isVoiceQuery,
    autoPlayResponse,
    setupVoiceRecognition,
    handleVoiceSearch,
    speakRealtimeText,
    primeAudioPlayback,
    prepareVoiceSession,
    cancelAssistantSpeech,
    cancelVoiceSession,
    startVoiceCapture,
    stopVoiceCapture,
    resetVoiceFlags,
  };
};
